import React, { useState, useEffect, useMemo } from "react";
// nodejs library that concatenates classes
import classNames from "classnames";
import { useRouter } from "next/router";
import { Subject, interval, defer, switchMap } from "rxjs";
import { filter, debounceTime } from "rxjs/operators";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormControl from "@material-ui/core/FormControl";
import TextField from "@material-ui/core/TextField";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
// @material-ui/icons
import Email from "@material-ui/icons/Email";
import PersonIcon from "@material-ui/icons/Person";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import DateRange from "@material-ui/icons/DateRange";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import IconButton from "@material-ui/core/IconButton";
// core components
import Header from "components/Header/Header.js";
import Footer from "components/Footer/Footer.js";
import Button from "components/CustomButtons/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {
	FormHelperText,
	Link,
	useMediaQuery,
	useTheme,
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TypeSelect from "components/Content/TypeSelect";
import {
	MuiPickersUtilsProvider,
	KeyboardDatePicker,
} from "@material-ui/pickers";
import {
	subYears,
	isValid,
	isBefore,
	isAfter,
	differenceInYears,
} from "date-fns";
import deLocale from "date-fns/locale/de";

import USPBlock from "components/Content/USPBlock.js";

import styles from "assets/jss/nextjs-material-kit/pages/loginPage.js";
import customStyles from "assets/jss/custom/customStyles.js";

import backgroundBabe from "assets/img/sugarbabe-reg-background.jpg";
import backgroundDaddy from "assets/img/sugardaddy-reg-background.jpg";
import backgroundSugar from "assets/img/sugardating-reg-background.jpg";
import backgroundMob from "assets/img/sugardating-background-mob.jpg";

const formatter = new DateFnsUtils();

const useStyles = makeStyles((theme) => ({
	...styles,
	...customStyles,
	popupIndicatorOpen: {
		transform: "none !important",
	},
	marginRight: {
		right: "12px",
	},
	formControl: {
		marginTop: "1px",
	},
	lineHeightTerms: {
		lineHeight: "1.2",
	},
}));

let emailRegex = new RegExp(
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);

export async function getStaticProps() {
	return {
		props: {},
	};
}

const userNameRegEx = new RegExp(/[^A-Za-zäÄöÖüÜ0-9\._\s\-]+/);
const shouldNotContainRegEx = new RegExp(/[\.\s\-]{2,}/);
const firstElShouldBeLetter = new RegExp(/^[A-Za-zäÄöÖüÜ]+/);

type ProfileType = "0" | "1";

export default function RegisterPage(props) {
	const router = useRouter();
	const [showPassword, setShowPassword] = useState(false);
	const handleClickShowPassword = () => setShowPassword(!showPassword);

	const theme = useTheme();
	const isSmUp = useMediaQuery(theme.breakpoints.up("sm"), {
		noSsr: true,
	});
	const maxDate = subYears(new Date(), 18);
	const minDate = subYears(new Date(), 120);
	const startDate = new Date(Date.now() - 1000 * 3600 * 24 * 365 * 26);

	const [userName, setUserName] = useState({
		value: "",
		valid: true,
		errorMsg: undefined,
	});
	const [email, setEmail] = useState({
		value: "",
		valid: true,
		errorMsg: undefined,
	});
	const [password, setPassword] = useState({
		value: "",
		valid: true,
		errorMsg: undefined,
	});

	const [location, setLocation] = useState(null);
	const [locationValid, setLocationValid] = useState(true);

	const [type, setType] = useState<ProfileType | "">("");
	const [typeValid, setTypeValid] = useState(true);

	const [options, setOptions] = useState<Location[]>([]);
	const [selectedDate, setSelectedDate] = useState(null);
	const [accepted, setAccepted] = useState(false);
	const [registerSuccess, setRegisterSuccess] = useState(false);
	const [isRegistering, setIsRegistering] = useState(false);
	const [error, setError] = useState("");
	const [subject] = useState<Subject<string>>(new Subject<string>());
	const [isPromo, setIsPromo] = useState(false);
	const [promo, setPromo] = useState("");

	const isDownXS = useMediaQuery(theme.breakpoints.down("xs"));

	const [cardAnimaton, setCardAnimation] = useState("cardHidden");
	setTimeout(function () {
		setCardAnimation("");
	}, 700);
	const classes = useStyles();
	const { ...rest } = props;

	useEffect(() => {
		setIsPromo(
			typeof window !== "undefined" &&
				(localStorage.getItem("currentPromo") === "sforum" ||
					localStorage.getItem("currentPromo") === "smaschke")
		);
		setPromo(
			typeof window !== "undefined" && localStorage.getItem("currentPromo")
		);
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		setType((params.get("type") as ProfileType) || "");

		const windowRef = window as any;

		const mapEventToGoal: { [k: string]: number } = {
			funnel_reg_form: 9,
		};

		windowRef._paq = windowRef._paq || [];
		windowRef._paq.push(["trackGoal", mapEventToGoal["funnel_reg_form"]]);

		windowRef._mtm = windowRef._mtm || [];
		windowRef._mtm.push({ event: "funnel_reg_form" });

		windowRef.dataLayer = windowRef.dataLayer || [];
		windowRef.dataLayer.push({ event: "funnel_reg_form" });
	}, []);

	useEffect(() => {
		let sub = subject
			.pipe(
				debounceTime(200),
				switchMap((query: string) =>
					defer(async () => {
						if (query.length < 2) {
							return [];
						}
						const url = "/api/location?query=" + encodeURIComponent(query);
						try {
							const response = await fetch(url);
							if (response.status === 200) {
								const res = await response.json();

								return res.data;
							} else {
								console.log("Login failed.");
								// https://github.com/developit/unfetch#caveats
								let error = new Error(response.statusText);
								(error as any).response = response;
								throw error;
							}
						} catch (error) {
							console.error(
								"You have an error in your code or there are Network issues.",
								error
							);
						}
					})
				)
			)
			.subscribe((suggestions: Location[]) => {
				setOptions(suggestions);
			});
		return () => {
			sub.unsubscribe();
		};
	}, [subject]);

	const handleChange = (event) => {
		subject.next(event.target.value);
	};

	useEffect(() => {
		if (registerSuccess) {
			let windowRef = window as any;

			let age = differenceInYears(Date.now(), selectedDate);

			let ar = "";
			if (age <= 29) {
				ar = "1829";
			} else if (age <= 39) {
				ar = "3039";
			} else if (age <= 49) {
				ar = "4049";
			} else if (age <= 59) {
				ar = "5059";
			} else if (age <= 69) {
				ar = "6069";
			} else if (age <= 79) {
				ar = "7079";
			} else if (age <= 89) {
				ar = "8089";
			} else {
				ar = "9000";
			}

			const mapEventToGoal: { [k: string]: number } = {
				funnel_reg_send: 10,
			};

			windowRef._paq = windowRef._paq || [];
			windowRef._paq.push(["setCustomDimension", 1, type === "0" ? "m" : "f"]);
			windowRef._paq.push(["trackGoal", mapEventToGoal["funnel_reg_send"]]);

			windowRef._mtm = windowRef._mtm || [];
			windowRef._mtm.push({
				event: "funnel_reg_send",
				gender: type === "0" ? "m" : "f",
				ar,
			});

			windowRef.dataLayer = windowRef.dataLayer || [];
			windowRef.dataLayer.push({
				event: "funnel_reg_send",
				gender: type === "0" ? "m" : "f",
				ar,
			});

			window.scrollTo({
				top: 0,
				behavior: "auto",
			});
		}
	}, [registerSuccess]);

	const handleDateChange = (date) => {
		setSelectedDate(date);
	};

	async function handleSubmit(event) {
		event.preventDefault();
		if (type === "") {
			setTypeValid(false);
			return;
		}

		if (userName.value === "") {
			setUserName((oldVal) => ({ ...oldVal, valid: false }));
			return;
		}

		if (email.value === "") {
			setEmail((oldVal) => ({ ...oldVal, valid: false }));
			return;
		}

		if (password.value === "") {
			setPassword((oldVal) => ({ ...oldVal, valid: false }));
			return;
		}

		if (!location) {
			setLocationValid(false);
		}

		const requirementsMet = checkRequirements();

		const isValidBool =
			userName.valid && email.valid && password.valid && requirementsMet;
		if (!isValidBool) {
			return;
		}

		setIsRegistering(true);
		setError("");
		try {
			let initialSource = localStorage.getItem("initial_source");
			let currentSource = localStorage.getItem("current_source");
			let sst = localStorage.getItem("current_sst");

			const response = await fetch("/api/register", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email: email.value,
					password: password.value,
					userName: userName.value.trim(),
					location,
					dateOfBirth: formatter.format(selectedDate, "yyyy-MM-dd"),
					type: type === "0" ? "PATRON" : "CREATOR",
					initialSource: initialSource ? JSON.parse(initialSource) : null,
					currentSource: currentSource ? JSON.parse(currentSource) : null,
					sst: sst ? JSON.parse(sst) : null,
					promo: localStorage.getItem("currentPromo"),
				}),
			});
			if (response.status === 201) {
				const res = await response.json();

				setRegisterSuccess(true);
				localStorage.removeItem("currentPromo");
				window.scrollTo({
					top: 0,
				});
				setError("");
			} else {
				const err = await response.json();
				setError(err.type);
				if (
					err.type === "validation" &&
					err.validation[0].path[0] === "email"
				) {
					setEmail((oldVal) => ({
						value: oldVal.value,
						valid: false,
						errorMsg: "Bitte eine andere Email-Adresse eingeben",
					}));
				}
			}
			setIsRegistering(false);
		} catch (error) {
			setIsRegistering(false);
			console.error(
				"You have an error in your code or there are Network issues.",
				error
			);
		}
	}

	function validateUserName(name) {
		let errorMsg =
			"Dein Name muss mindestens 3 Zeichen und maximal 16 Zeichen lang sein";
		let isValid = name === "" || (name.length >= 3 && name.length <= 16);
		if (isValid) {
			let match = userNameRegEx.exec(name);
			isValid = match === null;
			if (!isValid) {
				errorMsg =
					"Erlaubte Zeichen sind Buchstaben, Zahlen, Leerzeichen sowie . - _";
			}
		}
		if (isValid) {
			let match = shouldNotContainRegEx.exec(name);
			isValid = match === null;
			if (!isValid) {
				errorMsg = "Sonderzeichen dürfen nicht direkt hintereinander sein";
			}
		}
		if (isValid) {
			let match = firstElShouldBeLetter.exec(name);
			isValid = match !== null;
			if (!isValid) {
				errorMsg = "Der Name muss mit einem Buchstaben beginnen";
			}
		}
		setUserName({
			value: name,
			valid: isValid,
			errorMsg: !isValid ? errorMsg : undefined,
		});
	}

	function validateEmail(email) {
		let isValid = email === "" || emailRegex.test(email);
		setEmail({
			value: email,
			valid: isValid,
			errorMsg: !isValid
				? "Bitte eine gültige Email-Adresse eingeben"
				: undefined,
		});
	}

	function validatePassword(pwd) {
		let isValid = pwd === "" || pwd.length >= 5;
		setPassword({
			value: pwd,
			valid: isValid,
			errorMsg: !isValid
				? "Dein Passwort muss mindestens 5 Zeichen lang sein"
				: undefined,
		});
	}

	function checkRequirements() {
		return (
			userName.value !== "" &&
			userName.valid &&
			email.value !== "" &&
			email.valid &&
			password.value !== "" &&
			password.valid &&
			location !== null &&
			selectedDate !== null &&
			isValid(selectedDate) &&
			isBefore(selectedDate, maxDate) &&
			isAfter(selectedDate, minDate) &&
			accepted
		);
	}

	return (
		<div>
			<Header color="primary" fixed isPromo={isPromo} promo={promo} {...rest} />
			<div>
				<div
					className={classNames(classes.rowGrey)}
					style={{
						backgroundPositionY: "70px",
						backgroundImage:
							"url(" +
							(!isSmUp
								? backgroundMob.src
								: type === ""
								? backgroundSugar.src
								: type === "0"
								? backgroundDaddy.src
								: backgroundBabe.src) +
							")",
					}}
				>
					<div
						className={classNames(classes.container)}
						style={{ paddingTop: isDownXS? isPromo? "5.5rem" : "1.9rem" : 0 }}
					>
						<Grid container justify="center" spacing={2}>
							<Grid item xs={12} sm={8} md={6} lg={4}>
								<div
									className={classNames(
										classes.signUpBox,
										classes.formBox,
										classes.hasShadow
									)}
								>
									{registerSuccess && (
										<div style={{ padding: "0.5em 2em", paddingTop: 0 }}>
											<h4
												className={classNames(
													classes.isBold,
													classes.hasTextCentered
												)}
											>
												Anmeldung erfolgreich
											</h4>
											<p>
												Das hat geklappt! Wir haben dir eine E-Mail zur
												Aktivierung geschickt. Klicke dort auf den Link und
												flirte los!
											</p>
											<p>
												Falls keine E-Mail in deinem Postfach ist, schau auch in
												deinen Spam-Ordner.
											</p>

											<p className={classes.hasTextCentered}>
												<Button
													variant="contained"
													size="small"
													color="primary"
													onClick={() => {
														router.push("/login");
													}}
												>
													Zum Login
												</Button>
											</p>
										</div>
									)}
									{!registerSuccess && (
										<form onSubmit={handleSubmit}>
											<h4
												className={classNames(
													classes.isBold,
													classes.hasTextCentered
												)}
												style={{ paddingBottom: "0.75em" }}
											>
												{type === ""
													? "Jetzt anmelden & losflirten"
													: type === "1"
													? "Jetzt wohlhabende Männer kennenlernen"
													: "Jetzt attraktive Frauen kennenlernen"}
											</h4>
											<TypeSelect
												type={type}
												setType={(type) => {
													setType(type);
													setTypeValid(true);
												}}
												isError={!typeValid}
											/>

											<FormControl
												fullWidth
												margin="dense"
												className={classes.formControl}
											>
												<TextField
													error={!userName.valid || error === "userNameTaken"}
													helperText={
														!userName.valid
															? userName.errorMsg
															: error === "userNameTaken"
															? "Der Name ist bereits registiert"
															: error === "userNameInvalid"
															? "Erlaubte Zeichen sind Buchstaben, Zahlen, Leerzeichen sowie ,-"
															: "Dein echter Vorname macht dich authentisch, Pseudonyme werden aber ebenfalls akzeptiert"
													}
													id="userName"
													label="Gewünschter Name"
													fullWidth
													value={userName.value}
													onChange={(e) => {
														if (
															["userNameTaken", "userNameValid"].includes(error)
														) {
															setError("");
														}
														validateUserName(e.target.value);
													}}
													InputProps={{
														classes: {
															input: classes.input,
														},
														endAdornment: (
															<InputAdornment position="end">
																<IconButton>
																	<PersonIcon />
																</IconButton>
															</InputAdornment>
														),
													}}
												/>
											</FormControl>
											<FormControl
												fullWidth
												margin="dense"
												className={classes.formControl}
											>
												<TextField
													error={
														!email.valid ||
														error === "emailTaken" ||
														error === "emailTrash"
													}
													helperText={
														email.errorMsg
															? email.errorMsg
															: error === "emailTaken"
															? "E-Mail ist bereits registriert"
															: error === "emailTrash"
															? "Dieser E-Mail-Anbieter ist nicht erlaubt"
															: undefined
													}
													id="email"
													label="E-Mail"
													autoComplete="username"
													fullWidth
													value={email.value}
													onChange={(e) => {
														if (["emailTaken"].includes(error)) {
															setError("");
														}
														validateEmail(e.target.value);
													}}
													InputProps={{
														classes: {
															input: classes.input,
														},
														endAdornment: (
															<InputAdornment position="end">
																<IconButton>
																	<Email />
																</IconButton>
															</InputAdornment>
														),
													}}
												/>
											</FormControl>
											<FormControl
												fullWidth
												margin="dense"
												className={classes.formControl}
											>
												<TextField
													error={!password.valid}
													helperText={password.errorMsg}
													id="password"
													label="Passwort"
													autoComplete="new-password"
													type={showPassword ? "text" : "password"}
													fullWidth
													value={password.value}
													onChange={(e) => validatePassword(e.target.value)}
													InputProps={{
														classes: {
															input: classes.input,
														},
														endAdornment: (
															<InputAdornment position="end">
																<IconButton onClick={handleClickShowPassword}>
																	{showPassword ? (
																		<VisibilityIcon />
																	) : (
																		<VisibilityOffIcon />
																	)}
																</IconButton>
															</InputAdornment>
														),
													}}
												/>
											</FormControl>

											<FormControl
												fullWidth
												margin="dense"
												className={classes.formControl}
												error={!locationValid}
											>
												<Autocomplete
													id="location-suggest"
													popupIcon={
														<LocationOnIcon
															style={{ transform: "none !important" }}
														/>
													}
													forcePopupIcon
													noOptionsText="keine Ergebnisse"
													classes={{
														input: classes.input,
														groupLabel: classes.labelRoot,
														popupIndicatorOpen: classes.popupIndicatorOpen,
														endAdornment: classes.marginRight,
													}}
													getOptionLabel={(option) => {
														if (typeof option === "string") {
															return option;
														}
														if (option.suburb) {
															return `${option.city} - ${option.suburb}`;
														}
														return option.city;
													}}
													options={options}
													filterOptions={(x) => x}
													freeSolo
													clearOnBlur
													value={location || null}
													autoHighlight={true}
													onChange={(ev, selection, reason) => {
														if (
															reason === "select-option" &&
															selection &&
															typeof selection === "object"
														) {
															let val = {
																...selection,
																coordinates: {
																	latitude: selection.coordinates.latitude,
																	longitude: selection.coordinates.longitude,
																},
															};
															delete val.__typename;
															setLocation(val);
															setLocationValid(true);
														} else if (
															reason === "clear" ||
															reason === "remove-option"
														) {
															setLocation(null);
															setLocationValid(false);
															setOptions([]);
														}
													}}
													getOptionSelected={(option, value) => {
														if (typeof value === "string") {
															return false;
														}
														if (value.suburb) {
															return (
																option.city === value.city &&
																option.suburb === value.suburb
															);
														}
														return option.city === value.city;
													}}
													renderInput={(params) => (
														<TextField
															color="primary"
															{...params}
															label="Ort eingeben"
															fullWidth
															autoComplete="off"
															onChange={handleChange}
														/>
													)}
													renderOption={(option) => {
														return (
															<Grid container alignItems="center">
																<Grid item key="locationIcon">
																	<LocationOnIcon />
																</Grid>
																<Grid item xs key="locationName">
																	{option.suburb
																		? `${option.city} - ${option.suburb}`
																		: option.city}
																	<Typography
																		variant="body2"
																		color="textSecondary"
																	>
																		{option.country}
																	</Typography>
																</Grid>
															</Grid>
														);
													}}
												/>
												{!locationValid && (
													<FormHelperText>
														Bitte gib einen Ort an
													</FormHelperText>
												)}
											</FormControl>

											<MuiPickersUtilsProvider
												utils={DateFnsUtils}
												locale={deLocale}
											>
												<KeyboardDatePicker
													fullWidth
													autoOk
													disableToolbar
													openTo="date"
													variant="inline"
													format="dd.MM.yyyy"
													margin="normal"
													id="date-picker-inline"
													label="Geburtsdatum"
													invalidDateMessage="Das eingegebene Datum ist ungültig"
													initialFocusedDate={startDate}
													maxDate={maxDate}
													maxDateMessage="Du musst mindestens 18 Jahre sein um dich zu registrieren"
													minDate={minDate}
													minDateMessage="Du kannst nicht älter als 120 Jahre sein"
													views={["date", "month", "year"]}
													value={selectedDate}
													onChange={handleDateChange}
													InputProps={{
														classes: {
															input: classes.input,
															root: classes.formControl,
														},
														endAdornment: (
															<InputAdornment position="end">
																<DateRange />
															</InputAdornment>
														),
													}}
												/>
											</MuiPickersUtilsProvider>

											<FormControl fullWidth margin="dense">
												<FormControlLabel
													control={<Checkbox name="AGB" color="primary" />}
													onChange={(event) =>
														setAccepted((event.target as any).checked)
													}
													classes={{
														label: classes.lineHeightTerms,
													}}
													label={
														<span
															style={{
																fontSize: "0.75rem",
																lineHeight: "1.3",
																color: "rgba(0, 0, 0, 0.54)",
															}}
														>
															Hiermit bestätige ich die{" "}
															<Link
																href="/datenschutz"
																target="_blank"
																className={classes.isPrimary}
															>
																Datenschutzerklärung
															</Link>{" "}
															und die{" "}
															<Link
																href="/agb"
																target="_blank"
																className={classes.isPrimary}
															>
																AGB
															</Link>{" "}
															gelesen zu haben und akzeptiere diese.
														</span>
													}
												/>
											</FormControl>
											<Button
												color="primary"
												fullWidth
												type="submit"
												disabled={isRegistering}
											>
												{isRegistering
													? "wird gespeichert..."
													: "jetzt kostenlos registrieren"}
											</Button>
										</form>
									)}
								</div>
							</Grid>
						</Grid>
					</div>
				</div>
			</div>

			{!isDownXS && <USPBlock />}

			<Footer withSignup={false} />
		</div>
	);
}
